import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  MenuBook,
  ArrowDropDown,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  ArrowBack,
  Logout as LogoutIcon,
  Person,
  Inventory2,
  LocalDining,
  TableRestaurant,
  EditNote,
  PeopleAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useMenuStore } from '../../stores/menuStore.js';
import { InventoryPanel } from './InventoryPanel.jsx';
import { StaffPanel } from './StaffPanel.jsx';
import { usePendingTableRequests } from '../../hooks/usePendingTableRequests.js';
import platformLogo from '../../assets/LOGO_BGLESS.png';

export const SIDEBAR_EXPANDED_WIDTH = 264;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

export const LeftSidebar = ({ showDashboard, onToggleDashboard, collapsed, onToggleCollapse }) => {
  const { logout, accessType } = useAuth();
  const navigate = useNavigate();
  const { menus, selectedMenuId, setSelectedMenu } = useMenuStore();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showStaffPanel, setShowStaffPanel] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { pendingCount } = usePendingTableRequests();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      console.error('Logout failed:', e);
    }
  };

  const handleMenuSelect = (menuId) => {
    setSelectedMenu(menuId);
    setMenuAnchor(null);
  };

  const selectedMenuName =
    menus.find((m) => m.menu_id === selectedMenuId)?.menu_name || 'Select menu';

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  return (
    <Box
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        py: 0.5,
      }}
    >
      {/* ── Logo ────────────────────────────────────────────── */}
      <Box sx={{ px: collapsed ? 1 : 2, pt: 1, pb: 2, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <Box
          component="img"
          src={platformLogo}
          alt="Karobar"
          sx={{
            height: collapsed ? 28 : 32,
            width: 'auto',
            maxWidth: collapsed ? 40 : 140,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* ── Dashboard / Back to Home Button (Owner only) ────────────── */}
      {accessType === 'owner' && (
      <Box sx={{ px: collapsed ? 1 : 2, pb: 0.5 }}>
        {collapsed ? (
          <Tooltip title={showDashboard ? 'Back to Home' : 'Dashboard'} placement="right" arrow>
            <IconButton
              onClick={onToggleDashboard}
              sx={{
                width: '100%',
                borderRadius: '12px',
                ...(showDashboard
                  ? {
                      bgcolor: 'primary.main',
                      color: '#FFFFFF',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }
                  : {
                      color: 'text.primary',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: 'rgba(69, 39, 160, 0.06)',
                      },
                    }),
              }}
            >
              {showDashboard ? <ArrowBack sx={{ fontSize: 20 }} /> : <DashboardIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            startIcon={showDashboard ? <ArrowBack sx={{ fontSize: 16 }} /> : <DashboardIcon sx={{ fontSize: 16 }} />}
            onClick={onToggleDashboard}
            variant={showDashboard ? 'contained' : 'text'}
            disableElevation
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 1,
              justifyContent: 'flex-start',
              ...(showDashboard
                ? {
                    bgcolor: 'primary.main',
                    color: '#FFFFFF',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }
                : {
                    color: 'text.primary',
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: 'rgba(69, 39, 160, 0.06)',
                    },
                  }),
            }}
          >
            {showDashboard ? 'Back to Home' : 'Dashboard'}
          </Button>
        )}
      </Box>
      )}

      {/* ── Menus Row ─────────────────────────────────────── */}
      <Box sx={{ px: collapsed ? 1 : 2, pb: 0.5, display: 'flex', gap: 0.75 }}>
        {collapsed ? (
          <Tooltip title="Menus" placement="right" arrow>
            <IconButton
              onClick={() => navigate('/menu')}
              sx={{
                width: '100%',
                borderRadius: '12px',
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'rgba(69, 39, 160, 0.06)',
                },
              }}
            >
              <MenuBook sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Button
              onClick={() => navigate('/menu')}
              startIcon={<MenuBook sx={{ fontSize: 16 }} />}
              variant="text"
              disableElevation
              sx={{
                flex: 1,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                py: 1,
                justifyContent: 'flex-start',
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'rgba(69, 39, 160, 0.06)',
                },
              }}
            >
              Menus
            </Button>

            <Tooltip title={selectedMenuName} arrow>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  width: 36,
                  borderRadius: '10px',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: 'rgba(69, 39, 160, 0.06)' },
                }}
              >
                <ArrowDropDown
                  sx={{ fontSize: 20, color: menuAnchor ? '#4527A0' : '#86868B' }}
                />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Dropdown Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
              mt: 0.5,
              minWidth: 180,
            },
          }}
        >
          {menus.length === 0 ? (
            <MenuItem disabled sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>
              No menus available
            </MenuItem>
          ) : (
            menus.map((m) => (
              <MenuItem
                key={m.menu_id}
                selected={m.menu_id === selectedMenuId}
                onClick={() => handleMenuSelect(m.menu_id)}
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: m.menu_id === selectedMenuId ? 700 : 500,
                  py: 1,
                  px: 2,
                  borderRadius: '8px',
                  mx: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(69, 39, 160, 0.08)',
                    '&:hover': { bgcolor: 'rgba(69, 39, 160, 0.12)' },
                  },
                }}
              >
                {m.menu_name}
                {m.is_primary_menu === 'yes' && ' ★'}
              </MenuItem>
            ))
          )}
        </Menu>
      </Box>

      {/* ── Kitchen Button (Owner + Staff) ─────────────────── */}
      <Box sx={{ px: collapsed ? 1 : 2, pb: 0.5 }}>
        {collapsed ? (
          <Tooltip title="Kitchen" placement="right" arrow>
            <IconButton
              onClick={() => navigate('/kitchen')}
              sx={{
                width: '100%',
                borderRadius: '12px',
                color: 'text.primary',
                '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
              }}
            >
              <LocalDining sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            startIcon={<LocalDining sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/kitchen')}
            variant="text"
            disableElevation
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 1,
              justifyContent: 'flex-start',
              color: 'text.primary',
              '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
            }}
          >
            Kitchen
          </Button>
        )}
      </Box>

      {/* ── Tables Button (Owner + Staff) with notification ── */}
      <Box sx={{ px: collapsed ? 1 : 2, pb: 0.5 }}>
        {collapsed ? (
          <Tooltip title="Tables" placement="right" arrow>
            <IconButton
              onClick={() => navigate('/tables')}
              sx={{
                width: '100%',
                borderRadius: '12px',
                color: 'text.primary',
                '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
              }}
            >
              <Badge badgeContent={pendingCount} color="error" variant="dot" invisible={pendingCount === 0}>
                <TableRestaurant sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            startIcon={
              <Badge badgeContent={pendingCount} color="error" variant="dot" invisible={pendingCount === 0}>
                <TableRestaurant sx={{ fontSize: 16 }} />
              </Badge>
            }
            onClick={() => navigate('/tables')}
            variant="text"
            disableElevation
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 1,
              justifyContent: 'flex-start',
              color: 'text.primary',
              '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
            }}
          >
            Tables
          </Button>
        )}
      </Box>

      {/* ── Menu Management (Owner only) ───────────────────── */}
      {accessType === 'owner' && (
        <Box sx={{ px: collapsed ? 1 : 2, pb: 0.5 }}>
          {collapsed ? (
            <Tooltip title="Manage Menu" placement="right" arrow>
              <IconButton
                onClick={() => navigate('/menu-manage')}
                sx={{
                  width: '100%',
                  borderRadius: '12px',
                  color: 'text.primary',
                  '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
                }}
              >
                <EditNote sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              fullWidth
              startIcon={<EditNote sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/menu-manage')}
              variant="text"
              disableElevation
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                py: 1,
                justifyContent: 'flex-start',
                color: 'text.primary',
                '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
              }}
            >
              Manage Menu
            </Button>
          )}
        </Box>
      )}

      {/* ── Staff Button (Owner only) ──────────────────────── */}
      {accessType === 'owner' && (
        <Box sx={{ px: collapsed ? 1 : 2, pb: 0.5 }}>
          {collapsed ? (
            <Tooltip title="Staff" placement="right" arrow>
              <IconButton
                onClick={() => { setShowStaffPanel(!showStaffPanel); if (collapsed) onToggleCollapse(); }}
                sx={{
                  width: '100%',
                  borderRadius: '12px',
                  color: showStaffPanel ? 'primary.main' : 'text.primary',
                  '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' },
                }}
              >
                <PeopleAlt sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              fullWidth
              startIcon={<PeopleAlt sx={{ fontSize: 16 }} />}
              onClick={() => setShowStaffPanel(!showStaffPanel)}
              variant={showStaffPanel ? 'contained' : 'text'}
              disableElevation
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                py: 1,
                justifyContent: 'flex-start',
                ...(showStaffPanel
                  ? { bgcolor: 'primary.main', color: '#FFFFFF', '&:hover': { bgcolor: 'primary.dark' } }
                  : { color: 'text.primary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(69, 39, 160, 0.06)' } }),
              }}
            >
              Staff
            </Button>
          )}
          {/* Inline Staff Panel */}
          {showStaffPanel && !collapsed && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(69,39,160,0.03)', borderRadius: '12px', border: '1px solid rgba(69,39,160,0.08)' }}>
              <StaffPanel />
            </Box>
          )}
        </Box>
      )}

      {/* ── Inventory Panel (expanded) / Icon (collapsed) ──── */}
      {collapsed ? (
        <Box sx={{ px: 1, pb: 0.5 }}>
          <Tooltip title="Inventory" placement="right" arrow>
            <IconButton
              sx={{
                width: '100%',
                borderRadius: '12px',
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'rgba(69, 39, 160, 0.06)',
                },
              }}
            >
              <Inventory2 sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <InventoryPanel />
        </Box>
      )}

      {/* Spacer in collapsed mode */}
      {collapsed && <Box sx={{ flex: 1 }} />}

      {/* ── Settings Section ──────────────────────────────── */}
      <Box sx={{ px: collapsed ? 1 : 2, py: 1 }}>
        {collapsed ? (
          <Tooltip title="Settings" placement="right" arrow>
            <IconButton
              onClick={() => {
                setShowSettings(!showSettings);
                if (collapsed) onToggleCollapse();
              }}
              sx={{
                width: '100%',
                borderRadius: '12px',
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'rgba(69, 39, 160, 0.06)',
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            {showSettings && (
              <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Button
                  fullWidth
                  startIcon={<Person sx={{ fontSize: 16 }} />}
                  onClick={() => navigate('/profile')}
                  variant="text"
                  disableElevation
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    py: 0.8,
                    justifyContent: 'flex-start',
                    color: 'text.primary',
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: 'rgba(69, 39, 160, 0.06)',
                    },
                  }}
                >
                  Edit Profile
                </Button>
                <Button
                  fullWidth
                  startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setLogoutDialogOpen(true)}
                  variant="text"
                  disableElevation
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    py: 0.8,
                    justifyContent: 'flex-start',
                    color: '#D32F2F',
                    '&:hover': {
                      bgcolor: 'rgba(211,47,47,0.04)',
                    },
                  }}
                >
                  Logout
                </Button>
              </Box>
            )}
            <Button
              fullWidth
              startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowSettings(!showSettings)}
              variant={showSettings ? 'contained' : 'text'}
              disableElevation
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                py: 1,
                justifyContent: 'flex-start',
                ...(showSettings
                  ? {
                      bgcolor: 'primary.main',
                      color: '#FFFFFF',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }
                  : {
                      color: 'text.primary',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: 'rgba(69, 39, 160, 0.06)',
                      },
                    }),
              }}
            >
              Settings
            </Button>
          </>
        )}
      </Box>

      {/* ── Collapse / Expand Toggle ──────────────────────── */}
      <Box sx={{ px: collapsed ? 1 : 2, pb: 1.5 }}>
        {collapsed ? (
          <Tooltip title="Expand sidebar" placement="right" arrow>
            <IconButton
              onClick={onToggleCollapse}
              sx={{
                width: '100%',
                borderRadius: '12px',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <ChevronRight sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            startIcon={<ChevronLeft sx={{ fontSize: 16 }} />}
            onClick={onToggleCollapse}
            variant="text"
            disableElevation
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.78rem',
              py: 0.7,
              justifyContent: 'flex-start',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            Collapse
          </Button>
        )}
      </Box>

      {/* ── Logout Confirmation Dialog ────────────────────── */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '18px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            color="error"
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
